import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addProduct,
  getCalculatedDataPartner,
} from "../../../components/products/functions";
import { getUsersPartner } from "../../../components/users/functions";
import RegisterModal from "../../../components/users/registerModal";
import { partnerStatusObject } from "../../../components/utils/allProductStatus";
import {
  gradingTypes,
  gradingTypeSupportsAutograph,
  getGradingTypeLabel,
  getPreLacreFormatLabel,
  normalizeGradingType,
  preLacreFormatLabels,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  cashnator,
  cpfMask,
  decashnator,
  generateRandomID,
  isNumeric,
  phoneMask,
  formatarDataCompleta,
} from "../../../components/utils/functions";
import ToastContext from "../../../context/toastProvider";
import MobilePage from "../../ui/MobilePage";
import MobileSection from "../../ui/MobileSection";
import MobileStickyActions from "../../ui/MobileStickyActions";
import MobileFormField from "../../ui/MobileFormField";
import MobileSkeleton from "../../ui/MobileSkeleton";
import { useCreatePartnerProductWizard, isPartnerStepValid } from "./useCreatePartnerProductWizard";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";
const selectClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";

const STEP_TITLES = {
  user: "Usuário",
  product: "Dados do pedido",
  checkout: "Dados calculados",
  status: "Status",
  comments: "Comentários",
  summary: "Resumo",
};

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

export default function MobileCreatePartnerProduct({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { goodToast, badToast } = useContext(ToastContext);

  const prefillUser =
    location?.state?.origin === "users-new" ? location?.state?.user : null;

  const wizard = useCreatePartnerProductWizard({
    initialUserData: prefillUser || undefined,
    initialStep: prefillUser?._id ? "product" : "user",
    storeId: user?.store?._id,
  });

  const [submitting, setSubmitting] = useState(false);

  // user step
  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchCpf, setSearchCpf] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);

  // product step local
  const [gradingType, setGradingType] = useState(
    wizard.productData?.gradingType || "premium"
  );
  const [numberOfCards, setNumberOfCards] = useState(
    wizard.productData?.numberOfCards || ""
  );
  const [priority, setPriority] = useState(wizard.productData?.priority || "low");
  const [declaredValue, setDeclaredValue] = useState(
    cashnator(wizard.productData?.declaredValue) || "R$0,00"
  );
  const [autographEvaluation, setAutographEvaluation] = useState(
    !!wizard.productData?.autographEvaluation
  );
  const [preLacreFormat, setPreLacreFormat] = useState(
    wizard.productData?.preLacreFormat || "with_subgrades"
  );

  // checkout step local
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [insurance, setInsurance] = useState("R$0,00");
  const [finalPrice, setFinalPrice] = useState("R$0,00");
  const [deadline, setDeadline] = useState("");

  const searchUsers = useCallback(async () => {
    setSearchLoading(true);
    const data = await getUsersPartner(
      user?.token,
      searchName,
      searchEmail,
      searchCpf,
      searchPhone
    );
    setSearchLoading(false);
    if (data?.type !== "success") {
      badToast(data?.message ?? "Erro ao buscar usuários");
      setSearchResults([]);
      return;
    }
    setSearchResults(data.data ?? []);
  }, [user?.token, searchName, searchEmail, searchCpf, searchPhone, badToast]);

  const loadCalculatedData = useCallback(async () => {
    const pd = wizard.productData;
    if (!pd?.gradingType || !pd?.numberOfCards) return;
    setCheckoutLoading(true);
    const data = await getCalculatedDataPartner(
      user?.token,
      pd.gradingType,
      pd.numberOfCards,
      pd.priority,
      pd.insurance ?? 0,
      pd.autographEvaluation
    );
    setCheckoutLoading(false);
    if (data?.type !== "success") {
      badToast("Erro no cálculo do preço e prazo");
      return;
    }
    setInsurance(cashnator(data.data?.insurance ?? pd.insurance ?? 0));
    setFinalPrice(cashnator(data.data?.finalPrice));
    setDeadline(String(data.data?.deadline ?? ""));
    wizard.setProductData((current) => ({
      ...current,
      insurance: data.data?.insurance ?? current.insurance,
      finalPrice: data.data?.finalPrice,
      deadline: data.data?.deadline,
    }));
  }, [user?.token, wizard, badToast]);

  useEffect(() => {
    if (wizard.step === "checkout") {
      loadCalculatedData();
    }
  }, [wizard.step, loadCalculatedData]);

  const syncProductStep = () => {
    wizard.setProductData((current) => ({
      ...current,
      gradingType,
      numberOfCards,
      priority,
      declaredValue: decashnator(declaredValue),
      autographEvaluation:
        gradingTypeSupportsAutograph(gradingType) && autographEvaluation,
      ...(normalizeGradingType(gradingType) === "pre" ? { preLacreFormat } : {}),
    }));
  };

  const syncCheckoutStep = () => {
    wizard.setProductData((current) => ({
      ...current,
      insurance: decashnator(insurance),
      finalPrice: decashnator(finalPrice),
      deadline,
    }));
  };

  const canAdvance =
    wizard.step === "product"
      ? isPartnerStepValid("product", {
          productData: {
            gradingType,
            numberOfCards,
            priority,
            declaredValue: decashnator(declaredValue),
          },
        })
      : wizard.step === "checkout"
        ? Boolean(decashnator(finalPrice)) && Boolean(deadline)
        : wizard.canNext;

  const handleNext = () => {
    if (wizard.step === "product") syncProductStep();
    if (wizard.step === "checkout") syncCheckoutStep();
    wizard.next();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const { userData, deliveryData, productData } = wizard;
    const body = {
      localId: generateRandomID(),
      userId: userData?._id,
      gradingType: productData?.gradingType,
      autographEvaluation: !!productData?.autographEvaluation,
      ...(normalizeGradingType(productData?.gradingType) === "pre" &&
      productData?.preLacreFormat
        ? { preLacreFormat: productData.preLacreFormat }
        : {}),
      numberOfCards: productData?.numberOfCards,
      delivery: deliveryData?.delivery,
      priority: productData?.priority,
      cardsValue: productData?.declaredValue,
      insurance: productData?.insurance,
      store: deliveryData?.store,
      finalPrice: productData?.finalPrice,
      deadline: productData?.deadline,
      status: productData?.status,
      comments: [
        { date: new Date(), text: `Criado pelo admin por ${user?.email}` },
        ...(productData?.comments ?? []),
      ],
      createdByAdmin: true,
    };

    const data = await addProduct(user?.token, body, true);
    setSubmitting(false);
    if (data?.type !== "success") {
      badToast("Erro na criação de pedido");
      return;
    }
    goodToast("Pedido criado!");
    navigate(`/partner/products/${data.data}`);
  };

  const handleDeclaredValue = (value) => {
    if (!value) {
      setDeclaredValue("");
      return;
    }
    const newValue = decashnator(value);
    if (isNumeric(newValue)) {
      setDeclaredValue(cashnator(newValue));
    }
  };

  const addWizardComment = () => {
    const text = wizard.pendingComment.trim();
    if (!text) return;
    wizard.setProductData((current) => ({
      ...current,
      comments: [...(current.comments ?? []), { date: new Date(), text }],
    }));
    wizard.setPendingComment("");
  };

  const renderUserStep = () => (
    <MobileSection title="Buscar usuário">
      <MobileFormField label="Nome">
        <input
          className={inputClass}
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
      </MobileFormField>
      <MobileFormField label="E-mail">
        <input
          className={inputClass}
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
        />
      </MobileFormField>
      <MobileFormField label="CPF">
        <input
          className={inputClass}
          value={searchCpf}
          onChange={(e) => setSearchCpf(e.target.value)}
        />
      </MobileFormField>
      <MobileFormField label="Telefone">
        <input
          className={inputClass}
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
        />
      </MobileFormField>
      <button
        type="button"
        onClick={searchUsers}
        className="mb-3 inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
      >
        Pesquisar
      </button>
      <button
        type="button"
        onClick={() => setCreateUserOpen(true)}
        className="mb-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao"
      >
        Criar novo usuário
      </button>
      {searchLoading ? <MobileSkeleton lines={3} /> : null}
      <ul className="space-y-2">
        {searchResults.map((u) => (
          <li key={u._id}>
            <button
              type="button"
              onClick={() => wizard.setUserData(u)}
              className={`w-full rounded-lg border p-3 text-left text-sm ${
                wizard.userData?._id === u._id
                  ? "border-primary bg-primary/15"
                  : "border-primary/30 bg-mgs-bg"
              }`}
            >
              <p className="font-medium text-laranjinha">{u.name}</p>
              <p className="text-laranjinha/70">{u.email}</p>
            </button>
          </li>
        ))}
      </ul>
      {createUserOpen ? (
        <RegisterModal
          partner
          handleClose={() => setCreateUserOpen(false)}
          productCreation
          handleUser={(u) => {
            wizard.setUserData(u);
            setCreateUserOpen(false);
          }}
        />
      ) : null}
    </MobileSection>
  );

  const renderProductStep = () => (
    <MobileSection title="Dados do pedido">
      <MobileFormField label="Tipo">
        <select
          className={selectClass}
          value={gradingType}
          onChange={(e) => {
            const next = e.target.value;
            setGradingType(next);
            if (!gradingTypeSupportsAutograph(next)) setAutographEvaluation(false);
          }}
        >
          {Object.keys(gradingTypes).map((g) => (
            <option key={g} value={g}>
              {gradingTypes[g]}
            </option>
          ))}
        </select>
      </MobileFormField>
      <MobileFormField label="Número de cartas">
        <input
          className={inputClass}
          value={numberOfCards}
          onChange={(e) => {
            if (!e.target.value || isNumeric(e.target.value)) {
              setNumberOfCards(e.target.value);
            }
          }}
        />
      </MobileFormField>
      {gradingTypeSupportsAutograph(gradingType) ? (
        <label className="mb-3 flex min-h-[44px] items-center gap-3 text-sm text-laranjinha/80">
          <input
            type="checkbox"
            checked={autographEvaluation}
            onChange={(e) => setAutographEvaluation(e.target.checked)}
            className="h-5 w-5 rounded border-primary/50"
          />
          Avaliação de autógrafo
        </label>
      ) : null}
      {normalizeGradingType(gradingType) === "pre" ? (
        <MobileFormField label="Pré lacre">
          <select
            className={selectClass}
            value={preLacreFormat}
            onChange={(e) => setPreLacreFormat(e.target.value)}
          >
            {Object.keys(preLacreFormatLabels).map((k) => (
              <option key={k} value={k}>
                {preLacreFormatLabels[k]}
              </option>
            ))}
          </select>
        </MobileFormField>
      ) : null}
      <MobileFormField label="Prioridade">
        <select
          className={selectClass}
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          {Object.keys(priorityTypes).map((p) => (
            <option key={p} value={p}>
              {priorityTypes[p]}
            </option>
          ))}
        </select>
      </MobileFormField>
      <MobileFormField label="Valor declarado">
        <input
          className={inputClass}
          value={declaredValue}
          onChange={(e) => handleDeclaredValue(e.target.value)}
        />
      </MobileFormField>
    </MobileSection>
  );

  const renderCheckoutStep = () => (
    <MobileSection title="Dados calculados">
      {checkoutLoading ? (
        <MobileSkeleton lines={3} />
      ) : (
        <>
          <MobileFormField label="Taxa de manuseio">
            <input
              className={inputClass}
              value={insurance}
              onChange={(e) => {
                const v = decashnator(e.target.value);
                if (!e.target.value || isNumeric(v)) setInsurance(cashnator(v));
              }}
            />
          </MobileFormField>
          <MobileFormField label="Valor final">
            <input
              className={inputClass}
              value={finalPrice}
              onChange={(e) => {
                const v = decashnator(e.target.value);
                if (!e.target.value || isNumeric(v)) setFinalPrice(cashnator(v));
              }}
            />
          </MobileFormField>
          <MobileFormField label="Prazo (dias úteis)">
            <input
              className={inputClass}
              value={deadline}
              onChange={(e) => {
                if (!e.target.value || isNumeric(e.target.value)) {
                  setDeadline(e.target.value);
                }
              }}
            />
          </MobileFormField>
          <button
            type="button"
            onClick={loadCalculatedData}
            className="mt-2 inline-flex min-h-[44px] items-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
          >
            Recalcular
          </button>
        </>
      )}
    </MobileSection>
  );

  const renderStatusStep = () => (
    <MobileSection title="Status inicial">
      <select
        className={selectClass}
        value={wizard.productData?.status || "GENERATED"}
        onChange={(e) =>
          wizard.setProductData((current) => ({
            ...current,
            status: e.target.value,
          }))
        }
      >
        {Object.keys(partnerStatusObject).map((s) => (
          <option key={s} value={s}>
            {partnerStatusObject[s]}
          </option>
        ))}
      </select>
    </MobileSection>
  );

  const renderCommentsStep = () => (
    <MobileSection title="Comentários">
      {(wizard.productData?.comments ?? []).map((c, i) => (
        <p key={i} className="mb-2 rounded-lg border border-primary/30 px-3 py-2 text-sm">
          {c.text}
        </p>
      ))}
      <div className="mt-2 flex gap-2">
        <input
          className={inputClass}
          value={wizard.pendingComment}
          onChange={(e) =>
            wizard.setPendingComment(e.target.value.slice(0, 300))
          }
          placeholder="Novo comentário"
        />
        <button
          type="button"
          onClick={addWizardComment}
          disabled={!wizard.pendingComment.trim()}
          className="inline-flex min-h-[44px] items-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
        >
          Adicionar
        </button>
      </div>
    </MobileSection>
  );

  const renderSummaryStep = () => {
    const { userData, productData, deliveryData } = wizard;
    return (
      <MobileSection title="Resumo do pedido">
        <DetailRow label="Usuário" value={userData?.name} />
        <DetailRow label="E-mail" value={userData?.email} />
        <DetailRow label="Telefone" value={phoneMask(userData?.phone)} />
        <DetailRow
          label="Documento"
          value={
            userData?.documentType === "other"
              ? userData?.documentNumber
              : cpfMask(userData?.documentNumber)
          }
        />
        <DetailRow
          label="Tipo"
          value={getGradingTypeLabel(productData?.gradingType)}
        />
        <DetailRow
          label="Autógrafo"
          value={productData?.autographEvaluation ? "Sim" : "Não"}
        />
        {normalizeGradingType(productData?.gradingType) === "pre" &&
        productData?.preLacreFormat ? (
          <DetailRow
            label="Pré lacre"
            value={getPreLacreFormatLabel(productData.preLacreFormat)}
          />
        ) : null}
        <DetailRow label="Nº itens" value={String(productData?.numberOfCards)} />
        <DetailRow label="Prioridade" value={priorityTypes[productData?.priority]} />
        <DetailRow label="Entrega" value={deliveryData?.delivery} />
        <DetailRow label="Loja" value={user?.store?.name} />
        <DetailRow
          label="Valor declarado"
          value={cashnator(productData?.declaredValue)}
        />
        <DetailRow label="Taxa de manuseio" value={cashnator(productData?.insurance)} />
        <DetailRow label="Preço final" value={cashnator(productData?.finalPrice)} />
        <DetailRow label="Prazo" value={`${productData?.deadline} dias úteis`} />
        <DetailRow
          label="Status"
          value={partnerStatusObject[productData?.status]}
        />
        {(productData?.comments ?? []).map((c, i) => (
          <DetailRow
            key={i}
            label={formatarDataCompleta(c.date)}
            value={c.text}
          />
        ))}
      </MobileSection>
    );
  };

  const stepContent = {
    user: renderUserStep,
    product: renderProductStep,
    checkout: renderCheckoutStep,
    status: renderStatusStep,
    comments: renderCommentsStep,
    summary: renderSummaryStep,
  };

  if (!user?.store) {
    return (
      <MobilePage title="Criar pedido" onBack={() => navigate("/partner/products")}>
        <p className="px-4 py-8 text-center text-laranjinha/70">
          Você não possui um reino parceiro
        </p>
      </MobilePage>
    );
  }

  return (
    <MobilePage
      title={`Criar pedido — ${STEP_TITLES[wizard.step]}`}
      onBack={() => navigate("/partner/products")}
    >
      {stepContent[wizard.step]?.()}

      <MobileStickyActions>
        {wizard.canPrev ? (
          <button
            type="button"
            onClick={wizard.prev}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao"
          >
            Voltar
          </button>
        ) : null}
        {wizard.step === "summary" ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-green-700 px-4 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-50"
          >
            {submitting ? "Criando…" : "Criar pedido"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canAdvance}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
          >
            Avançar
          </button>
        )}
      </MobileStickyActions>
    </MobilePage>
  );
}
